/**
 * «DATOS EN ESTE MÓVIL»: una fila en el menú de la cuenta que dice cuándo se
 * guardó todo por última vez y cuánto ocupa, con un botón para descargarlo ya
 * (antes de entrar en un invernadero sin cobertura, por ejemplo).
 */
import { useEffect, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { HardDriveDownload, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { leerEstadoDescarga, resumenDescarga, sincronizar, suscribirEstadoDescarga } from "@/lib/offline/sincronizacionOffline";
import { leerEstadoOffline, suscribirEstadoOffline } from "@/lib/offline/estadoOffline";
import { cn } from "@/lib/utils";

function haceCuanto(ms: number, t: (k: string, o?: Record<string, unknown>) => string): string {
  const min = Math.round((Date.now() - ms) / 60000);
  if (min < 1) return t("offline.haceUnMomento");
  if (min < 60) return t("offline.haceMinutos", { count: min });
  const h = Math.round(min / 60);
  if (h < 24) return t("offline.haceHoras", { count: h });
  return t("offline.haceDias", { count: Math.round(h / 24) });
}

export function DatosEnEsteMovil({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { company, role } = useAuth();
  const descarga = useSyncExternalStore(suscribirEstadoDescarga, leerEstadoDescarga, leerEstadoDescarga);
  const red = useSyncExternalStore(suscribirEstadoOffline, leerEstadoOffline, leerEstadoOffline);
  const [usado, setUsado] = useState<number | null>(null);

  useEffect(() => {
    let vivo = true;
    void resumenDescarga().then((r) => vivo && setUsado(r.usado));
    return () => {
      vivo = false;
    };
  }, [descarga.ultima, descarga.enCurso]);

  const subtitulo = descarga.enCurso
    ? t(`offline.fase.${descarga.fase ?? "datos"}`) + (descarga.total ? ` · ${descarga.hechas}/${descarga.total}` : "")
    : [
        descarga.ultima ? t("offline.guardadoHace", { cuando: haceCuanto(descarga.ultima, t) }) : t("offline.nuncaDescargado"),
        usado ? t("offline.ocupa", { mb: (usado / 1024 / 1024).toFixed(1) }) : null,
      ]
        .filter(Boolean)
        .join(" · ");

  const puede = !!company?.id && red.enLinea && !descarga.enCurso;
  // Solo con el interruptor de la empresa encendido.
  if (!red.activo) return null;

  return (
    <div className={cn("flex items-center gap-3 rounded-2xl border border-[#b6dded] bg-white px-4 py-3 shadow-sm", className)}>
      <HardDriveDownload className="h-5 w-5 shrink-0 text-[#2B7DE0]" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-foreground">{t("offline.titulo")}</p>
        <p className="truncate text-xs text-muted-foreground" data-testid="datos-movil-estado">
          {subtitulo}
        </p>
      </div>
      <button
        type="button"
        disabled={!puede}
        onClick={() => company?.id && void sincronizar({ empresa: company.id, rol: role, forzar: true })}
        className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#b6dded] px-3 py-1.5 text-xs font-semibold text-[#2B7DE0] transition-all active:scale-95 disabled:opacity-50"
      >
        {descarga.enCurso ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
        {descarga.enCurso ? t("offline.descargando") : t("offline.descargarAhora")}
      </button>
    </div>
  );
}
